import PropTypes from '~/lib/propTypes'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import { useState } from 'react'
import { AUTHENTICATE_CLIENT_FOR_TOKEN } from '~/graphql/mutations/authenticateClientForToken'
import { useMutation } from '@apollo/client'
import {
  space,
  OneTimeCodeInputV2 as OneTimeCodeInput,
  Button,
  Alert,
  Checkbox,
  InputV2 as Input,
  Text,
  Instruction,
} from '@ticketswap/solar'
import styled from '@emotion/styled'
import getConfig from 'next/config'
import { REGISTER_USER_USING_CODE } from '~/graphql/mutations/registerUserUsingCode'
import { setTrackingIdentity, track } from '~/utils/analytics'
import { GIVE_USER_CONSENT } from '~/graphql/mutations/giveUserConsent'
import { AUTHENTICATION_COMPLETED } from '~/lib/tracking-events'
import { createMarkup } from '~/utils/createMarkup'
import { setCookie } from 'cookies-next'
import { MONTH_IN_SECONDS } from '~/lib/timeInSeconds'

const { publicRuntimeConfig } = getConfig()

const Form = styled.form`
  display: grid;
  grid-gap: ${space[16]};
`

const Names = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${space[8]};
`

const SignupWithCode = ({ t, onSignupComplete }) => {
  const { email } = useLoggedInUser()
  const [code, setCode] = useState(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [marketingConsentGiven, setMarketingConsentGiven] = useState(false)

  const [getAuthenticationToken, { client }] = useMutation(AUTHENTICATE_CLIENT_FOR_TOKEN)
  const [signup] = useMutation(REGISTER_USER_USING_CODE)
  const [giveConsent] = useMutation(GIVE_USER_CONSENT)

  const handleSignup = async event => {
    event.preventDefault()
    setError(null)
    setLoading(true)

    const {
      data: {
        authenticateClientForToken: { token },
      },
    } = await getAuthenticationToken({
      variables: {
        input: {
          clientId: publicRuntimeConfig.NEXT_PUBLIC_CLIENT_ID,
        },
      },
    })

    const { data } = await signup({
      onError: async () => {
        setError({
          message: t('common:generic.somethingWentWrong'),
        })

        await track(AUTHENTICATION_COMPLETED, {
          method: 'code',
          isNew: true,
          error: 'SOMETHING_WENT_WRONG',
        })

        setLoading(false)
      },
      variables: {
        input: {
          email,
          firstName,
          lastName,
          code,
          token,
        },
      },
    })

    if (!data) return

    const {
      registerUserUsingCode: { errors, apiAccessToken },
    } = data

    if (errors.length) {
      setLoading(false)

      await track(AUTHENTICATION_COMPLETED, {
        method: 'code',
        isNew: true,
        error: errors[0].code,
      })

      return setError(errors[0])
    }

    const { tokenString } = apiAccessToken

    setCookie(process.env.NEXT_PUBLIC_TOKEN_COOKIE_NAME, tokenString, {
      maxAge: MONTH_IN_SECONDS,
      secure: true,
      sameSite: 'lax',
    })

    await client.resetStore()

    await setTrackingIdentity()

    if (marketingConsentGiven) {
      await giveConsent({
        variables: {
          input: {
            consentType: 'MARKETING_EMAIL',
            hasGivenConsent: true,
          },
        },
      })
    }

    await track(AUTHENTICATION_COMPLETED, {
      method: 'code',
      isNew: true,
    })

    onSignupComplete('code', false, true)
  }

  const canSubmit = code?.length === 6 && firstName.length && lastName.length

  return (
    <Form onSubmit={handleSignup}>
      <Text
        as="p"
        dangerouslySetInnerHTML={createMarkup(
          t('common:auth.code.signup', {
            email,
          }),
          {
            renderInline: true,
            disableAutolink: true,
          }
        )}
      />

      <Names>
        <Input
          label={t('common:auth.signupEmail.fields.firstname.label')}
          type="text"
          id="firstname"
          name="firstname"
          value={firstName}
          onChange={event => setFirstName(event.target.value)}
        />

        <Input
          label={t('common:auth.signupEmail.fields.lastname.label')}
          type="text"
          id="lastname"
          name="lastname"
          value={lastName}
          onChange={event => setLastName(event.target.value)}
        />
      </Names>

      <div>
        <OneTimeCodeInput
          length={6}
          id="one-time-code-input"
          legend={t('common:auth.code.label')}
          aria-label={t('common:auth.code.ariaLabel')}
          onChange={event => setCode(event.target.value)}
          instruction={<Instruction>{t('common:auth.code.help')}</Instruction>}
        />
      </div>

      <Checkbox
        id="marketingConsent"
        name="marketingConsent"
        label={t('common:auth.consent.label')}
        onChange={event => setMarketingConsentGiven(event.target.checked)}
      />

      {error && <Alert variant="error">{error.message}</Alert>}

      <Button loading={loading} variant="primary" disabled={!canSubmit}>
        {t('common:auth.signupEmail.button')}
      </Button>
    </Form>
  )
}

SignupWithCode.propTypes = {
  t: PropTypes.func.isRequired,
  onSignupComplete: PropTypes.func.isRequired,
}

export default SignupWithCode
